import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ClipboardList, HeartHandshake, Mail, Users, ShieldCheck,
  LogOut, Home as HomeIcon, UserCircle, CheckSquare, DollarSign, FileText,
  AlertTriangle, Clock, Calendar, User as UserIcon, Menu, X, CalendarClock, ListChecks, Landmark,
} from "lucide-react";
import Logo from "@/components/layout/Logo";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

// full sidebar list by role
function itemsForRole(role) {
  if (role === "peer_worker") {
    return [
      { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
      { to: "/dashboard/todos", label: "To-dos", icon: ListChecks },
      { to: "/dashboard/clients", label: "My Clients", icon: UserCircle },
      { to: "/dashboard/calendar", label: "My Calendar", icon: Calendar },
      { to: "/dashboard/roster", label: "My Roster", icon: CalendarClock },
      { to: "/dashboard/tasks", label: "My Tasks", icon: CheckSquare },
      { to: "/dashboard/shifts", label: "My Shifts", icon: Clock },
      { to: "/dashboard/incidents", label: "Incident Reports", icon: AlertTriangle },
      { to: "/dashboard/profile", label: "My Profile", icon: UserIcon },
    ];
  }
  return [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/dashboard/todos", label: "To-dos", icon: ListChecks },
    { to: "/dashboard/referrals", label: "Referrals", icon: ClipboardList },
    { to: "/dashboard/clients", label: "Clients", icon: UserCircle },
    { to: "/dashboard/calendar", label: "Calendar", icon: Calendar },
    { to: "/dashboard/roster", label: "Roster", icon: CalendarClock },
    { to: "/dashboard/tasks", label: "Tasks", icon: CheckSquare },
    { to: "/dashboard/billables", label: "Billables", icon: DollarSign },
    { to: "/dashboard/grants", label: "Grants", icon: Landmark },
    { to: "/dashboard/incidents", label: "Incidents", icon: AlertTriangle },
    { to: "/dashboard/shifts", label: "My Shifts", icon: Clock },
    { to: "/dashboard/volunteers", label: "Volunteers", icon: HeartHandshake },
    { to: "/dashboard/members", label: "Members", icon: Users },
    { to: "/dashboard/contacts", label: "Contacts", icon: Mail },
    { to: "/dashboard/profile", label: "My Profile", icon: UserIcon },
  ];
}

// The bottom mobile tab bar: 5 highest-value destinations per role.
function mobileTabsForRole(role) {
  if (role === "peer_worker") {
    return [
      { to: "/dashboard", label: "Home", icon: LayoutDashboard, end: true },
      { to: "/dashboard/clients", label: "Clients", icon: UserCircle },
      { to: "/dashboard/roster", label: "Roster", icon: CalendarClock },
      { to: "/dashboard/shifts", label: "Shifts", icon: Clock },
      { to: "/dashboard/incidents", label: "Incidents", icon: AlertTriangle },
    ];
  }
  return [
    { to: "/dashboard", label: "Home", icon: LayoutDashboard, end: true },
    { to: "/dashboard/referrals", label: "Referrals", icon: ClipboardList },
    { to: "/dashboard/clients", label: "Clients", icon: UserCircle },
    { to: "/dashboard/roster", label: "Roster", icon: CalendarClock },
    { to: "/dashboard/shifts", label: "Shifts", icon: Clock },
  ];
}

const API_ROOT = process.env.REACT_APP_BACKEND_URL + "/api";

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [activeShift, setActiveShift] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const items = itemsForRole(user?.role);
  const mobileTabs = mobileTabsForRole(user?.role);
  const [avatarTs] = useState(Date.now());

  useEffect(() => {
    api.get("/me/shifts/active").then((r) => setActiveShift(r.data)).catch(() => {});
    const t = setInterval(() => api.get("/me/shifts/active").then((r) => setActiveShift(r.data)).catch(() => {}), 60000);
    return () => clearInterval(t);
  }, []);

  const signOut = async () => { await logout(); nav("/login"); };
  const photoSrc = user?.photo_path ? `${API_ROOT}/users/${user.id}/photo?v=${avatarTs}` : null;

  const sidebarNav = (onNavigate) => (
    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          onClick={onNavigate}
          data-testid={`sidebar-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive ? "bg-njas-sage-tint text-njas-navy" : "text-njas-muted hover:bg-njas-bg-alt hover:text-njas-navy"
            }`
          }
        >
          <it.icon className="w-4 h-4" /> {it.label}
        </NavLink>
      ))}
      {user?.role === "admin" && (
        <>
          <div className="pt-4 mt-3 border-t border-[var(--njas-border)]">
            <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-njas-muted">Admin</div>
          </div>
          {[
            { to: "/dashboard/directors", label: "Directors", icon: Users, testid: "directors" },
            { to: "/dashboard/peer-workers", label: "Peer Workers", icon: UserIcon, testid: "peer-workers" },
            { to: "/dashboard/form-templates", label: "Form Templates", icon: FileText, testid: "form-templates" },
            { to: "/dashboard/staff", label: "Staff Admin", icon: ShieldCheck, testid: "staff" },
          ].map((it) => (
            <NavLink key={it.to} to={it.to} onClick={onNavigate} data-testid={`sidebar-${it.testid}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-njas-sage-tint text-njas-navy" : "text-njas-muted hover:bg-njas-bg-alt hover:text-njas-navy"
              }`}>
              <it.icon className="w-4 h-4" /> {it.label}
            </NavLink>
          ))}
        </>
      )}
    </nav>
  );

  const sidebarFooter = (
    <div className="p-3 border-t border-[var(--njas-border)] space-y-1">
      <Link to="/dashboard/shifts" onClick={() => setDrawerOpen(false)} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${activeShift ? "bg-emerald-50 text-emerald-700" : "text-njas-muted"}`}>
        <span className={`w-2 h-2 rounded-full ${activeShift ? "bg-emerald-500 animate-pulse" : "bg-gray-300"}`} />
        {activeShift ? "On shift" : "Not clocked in"}
      </Link>
      <div className="flex items-center gap-2 px-3 py-2 text-xs text-njas-muted">
        {photoSrc ? <img src={photoSrc} alt="me" className="w-7 h-7 rounded-full object-cover" /> : <div className="w-7 h-7 rounded-full bg-njas-sage-tint text-njas-sage-hover flex items-center justify-center font-semibold text-xs">{(user?.name || "?").slice(0, 1)}</div>}
        <div className="min-w-0 flex-1">
          <div className="truncate text-njas-navy font-medium text-[13px]">{user?.name}</div>
          <div className="capitalize">{(user?.role || "").replace("_", " ")}</div>
        </div>
      </div>
      <Link to="/" onClick={() => setDrawerOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-njas-muted hover:bg-njas-bg-alt hover:text-njas-navy">
        <HomeIcon className="w-4 h-4" /> Public site
      </Link>
      <button onClick={signOut} data-testid="sign-out" className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-njas-muted hover:bg-njas-bg-alt hover:text-njas-navy">
        <LogOut className="w-4 h-4" /> Sign out
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-njas-bg-alt flex">
      {/* Desktop sidebar */}
      <aside className="w-56 shrink-0 bg-white border-r border-[var(--njas-border)] hidden lg:flex flex-col">
        <div className="p-5 border-b border-[var(--njas-border)]">
          <Link to="/"><Logo /></Link>
        </div>
        {sidebarNav()}
        {sidebarFooter}
      </aside>

      {/* Mobile slide-out drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white flex flex-col shadow-2xl" data-testid="mobile-drawer">
            <div className="p-4 border-b border-[var(--njas-border)] flex items-center justify-between">
              <Link to="/" onClick={() => setDrawerOpen(false)}><Logo /></Link>
              <button onClick={() => setDrawerOpen(false)} data-testid="mobile-drawer-close" className="p-1.5 rounded-lg hover:bg-njas-bg-alt">
                <X className="w-5 h-5 text-njas-muted" />
              </button>
            </div>
            {sidebarNav(() => setDrawerOpen(false))}
            {sidebarFooter}
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Header */}
        <header className="bg-white border-b border-[var(--njas-border)] px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 sticky top-0 z-30">
          {/* Left: mobile menu button + welcome */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setDrawerOpen(true)}
              data-testid="mobile-menu-btn"
              className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-njas-bg-alt"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5 text-njas-navy" />
            </button>
            <div className="min-w-0">
              <div className="eyebrow text-njas-sage-hover text-[10px] hidden sm:block">Staff Portal</div>
              <div className="text-sm sm:text-lg font-serif text-njas-navy truncate">
                <span className="hidden sm:inline">Welcome back, </span>{user?.name?.split(" ")[0]}
              </div>
            </div>
          </div>

          {/* Right: clock chip + avatar */}
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/dashboard/shifts"
              data-testid="header-shift-widget"
              className={`inline-flex items-center gap-1.5 rounded-full px-3 sm:px-4 py-1.5 text-xs font-medium ${activeShift ? "bg-emerald-50 text-emerald-700" : "bg-njas-bg-alt text-njas-muted hover:text-njas-navy"}`}
            >
              <Clock className="w-3.5 h-3.5" />
              <span className="hidden xs:inline sm:inline">{activeShift ? "On shift" : "Clock in"}</span>
            </Link>
            <Link to="/dashboard/profile" className="flex items-center gap-2 hover:bg-njas-bg-alt rounded-full px-1 sm:px-2 py-1">
              {photoSrc ? <img src={photoSrc} alt="me" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-njas-navy text-white flex items-center justify-center font-medium text-sm">{(user?.name || "?").slice(0, 1)}</div>}
              <div className="text-right hidden sm:block">
                <div className="text-xs font-medium text-njas-navy">{user?.name}</div>
                <div className="text-[10px] text-njas-muted capitalize">{(user?.role || "").replace("_", " ")}</div>
              </div>
            </Link>
          </div>
        </header>

        {/* Content area — extra bottom padding on mobile to clear the tab bar */}
        <div className="p-4 sm:p-6 pb-24 lg:pb-6 flex-1"><Outlet /></div>
      </main>

      {/* Mobile bottom tab bar */}
      <nav
        data-testid="mobile-bottom-nav"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[var(--njas-border)] flex justify-around pb-[env(safe-area-inset-bottom)]"
      >
        {mobileTabs.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            data-testid={`mobile-tab-${it.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors ${
                isActive ? "text-njas-navy" : "text-njas-muted"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <it.icon className={`w-5 h-5 ${isActive ? "text-njas-sage-hover" : ""}`} />
                <span>{it.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <PWAInstallPrompt />
    </div>
  );
}
